import { useCallback, useState } from 'react';
import { BangKeCongNoTheoHoaDonSearchFormValues } from '@/types/schemas';
import { useBangKeCongNoTheoHoaDon } from './useBangKeCongNoTheoHoaDon';
import { initialSearchValues } from '../schema';
import { useTableData } from './useTableData';

export interface UseReportDataReturn {
  // Dialog states
  initialSearchDialogOpen: boolean;
  editPrintTemplateDialogOpen: boolean;
  showTable: boolean;
  searchParams: BangKeCongNoTheoHoaDonSearchFormValues;

  // Dialog handlers
  handleInitialSearchClose: () => void;
  handleInitialSearch: (data: BangKeCongNoTheoHoaDonSearchFormValues) => Promise<void>;
  handleSearchClick: () => void;
  handleEditPrintTemplateClick: () => void;
  handleClosePrintTemplateDialog: () => void;
  handleSavePrintTemplate: (template: MuaHangRecord) => void;

  // Table data
  tables: MuaHangRecord[];
  handleRowClick: (params: MuaHangRecord) => void;
  isLoading: boolean;
  error: Error | null;
  refreshData: () => Promise<void>;

  // Action handlers
  handleRefreshClick: () => Promise<void>;
  handleFixedColumnsClick: () => void;
  handleExportDataClick: () => void;
}

export function useReportData(): UseReportDataReturn {
  const [initialSearchDialogOpen, setInitialSearchDialogOpen] = useState(true);
  const [editPrintTemplateDialogOpen, setEditPrintTemplateDialogOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [searchParams, setSearchParams] = useState<BangKeCongNoTheoHoaDonSearchFormValues>(initialSearchValues);

  // Data hooks
  const { data, isLoading, error, fetchData, refreshData } = useBangKeCongNoTheoHoaDon(searchParams);
  const { tables, handleRowClick } = useTableData(data, searchParams);

  // Dialog handlers
  const handleInitialSearchClose = useCallback(() => {
    setInitialSearchDialogOpen(false);
  }, []);

  const handleInitialSearch = useCallback(
    async (searchData: BangKeCongNoTheoHoaDonSearchFormValues) => {
      setSearchParams(searchData);
      setInitialSearchDialogOpen(false);
      try {
        await fetchData(searchData);
        setShowTable(true);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    },
    [fetchData]
  );

  const handleSearchClick = useCallback(() => {
    setInitialSearchDialogOpen(true);
  }, []);

  const handleEditPrintTemplateClick = useCallback(() => {
    setEditPrintTemplateDialogOpen(true);
  }, []);

  const handleClosePrintTemplateDialog = useCallback(() => {
    setEditPrintTemplateDialogOpen(false);
  }, []);

  const handleSavePrintTemplate = useCallback((template: MuaHangRecord) => {
    console.log('Saving print template:', template);
    setEditPrintTemplateDialogOpen(false);
  }, []);

  // Action handlers
  const handleRefreshClick = useCallback(async () => {
    await refreshData();
  }, [refreshData]);

  const handleFixedColumnsClick = useCallback(() => {
    console.log('Fixed columns clicked');
  }, []);

  const handleExportDataClick = useCallback(() => {
    console.log('Export data clicked');
  }, []);

  return {
    // Dialog states
    initialSearchDialogOpen,
    editPrintTemplateDialogOpen,
    showTable,
    searchParams,

    // Dialog handlers
    handleInitialSearchClose,
    handleInitialSearch,
    handleSearchClick,
    handleEditPrintTemplateClick,
    handleClosePrintTemplateDialog,
    handleSavePrintTemplate,

    // Table data
    tables,
    handleRowClick,
    isLoading,
    error,
    refreshData,

    // Action handlers
    handleRefreshClick,
    handleFixedColumnsClick,
    handleExportDataClick
  };
}
